import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";

const services: {
  id: number;
  icon: string;
  title: string;
  subtitle: string;
  image?: string;
  imageCaption?: string;
  imagePosition?: string;
  description: string;
  features: string[];
  hideFeatures?: boolean;
}[] = [
  {
    id: 1,
    icon: "ri-radar-line",
    title: "海底ケーブル敷設工事",
    subtitle: "Submarine Cable Installation",
    image: "https://static.readdy.ai/image/8e81880eb50dc98f95be220d1124e175/b836497fb1c2f1ab0181b75d9ff6955b.jpeg",
    imageCaption: "ケーブル船から赤ブイを連ねて海底ケーブルを布設中。",
    hideFeatures: true,
    description:
      "海底送電・通信ケーブルのルート調査から布設・防護管取り付け・埋設作業・ブイカットまで一貫施工。潜水士とSVが連携し、安全かつ確実な工事を実現します。",
    features: [],
  },
  {
    id: 2,
    icon: "ri-shield-check-line",
    title: "防食工事",
    subtitle: "Anti-Corrosion Work",
    image: "https://static.readdy.ai/image/8e81880eb50dc98f95be220d1124e175/2f74bc9d18be90f72c980e97c8b54778.jpeg",
    imagePosition: "object-left",
    hideFeatures: true,
    description: "構造物の腐食を防ぐ工事を安全かつ確実な工事を実現します。",
    features: [],
  },
  {
    id: 3,
    icon: "ri-anchor-line",
    title: "港湾工事",
    subtitle: "Revetment Construction",
    image: "https://static.readdy.ai/image/8e81880eb50dc98f95be220d1124e175/6c6c7472a664180fe368edf5ceda3122.jpeg",
    imagePosition: "object-top",
    hideFeatures: true,
    description:
      "足場設置から溶接、切断、工事一式を請け負い、安全かつ確実な工事を実現します。",
    features: [],
  },
  {
    id: 4,
    icon: "ri-ship-line",
    title: "サルベージ作業",
    subtitle: "Salvage Operation",
    image: "https://static.readdy.ai/image/8e81880eb50dc98f95be220d1124e175/397ecc7ac377b8cddc4b160a5b26a171.webp",
    imagePosition: "object-center",
    hideFeatures: true,
    description: "サルベージに関わる作業全般をサポートします。",
    features: [],
  },
  {
    id: 5,
    icon: "ri-user-search-line",
    title: "SV対応業務",
    subtitle: "Supervision Services",
    image: "https://public.readdy.ai/ai/img_res/edited_3870508ab647b523eb53c63d698e6af2_eb036d19.jpg",
    imagePosition: "object-top",
    hideFeatures: true,
    description: "海外現地から船上までSV業務にまつわる業務全般を行います。",
    features: [],
  },
  {
    id: 6,
    icon: "ri-flashlight-line",
    title: "電気通信工事",
    subtitle: "Electrical & Telecommunications",
    image: "https://static.readdy.ai/image/8e81880eb50dc98f95be220d1124e175/c670a91b47c21c3a8cbfeae549c158d8.png",
    imagePosition: "object-center",
    hideFeatures: true,
    description:
      "海底ケーブル電気通信、移動体通信、住宅機器取付工事まで幅広くご対応いたします。",
    features: [],
  },
  {
    id: 7,
    icon: "ri-more-line",
    title: "その他",
    subtitle: "Other Services",
    hideFeatures: true,
    description: "上記以外にも水中のことはお気軽にお問い合わせください。",
    features: [],
  },
];

export default function ServicesPage() {
  usePageTitle("事業内容");
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        const navHeight = 80;
        const top = element.getBoundingClientRect().top + window.scrollY - navHeight;
        window.scrollTo({ top, behavior: "smooth" });
      }
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-ocean-800">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 text-xs font-semibold tracking-widest text-ocean-300 border border-ocean-400 rounded-full mb-4">
                OUR SERVICES
              </span>
              <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4">
                事業内容
              </h1>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-ocean-50">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto space-y-6">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  id={
                    service.id === 1 ? "cable" :
                    service.id === 2 ? "anticorrosion" :
                    service.id === 3 ? "harbor" :
                    service.id === 4 ? "salvage" :
                    service.id === 5 ? "sv" :
                    service.id === 6 ? "electrical" :
                    service.id === 7 ? "other" : undefined
                  }
                  className="bg-white rounded-2xl p-6 md:p-10 border border-ocean-100"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                    <div className="md:col-span-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-ocean-50 text-ocean-500">
                          <i className={`${service.icon} text-2xl`} />
                        </div>
                        <span className="text-3xl font-bold text-ocean-100 font-serif">
                          0{index + 1}
                        </span>
                      </div>
                      <p className="text-xs font-semibold text-ocean-400 tracking-wider mb-1">
                        {service.subtitle}
                      </p>
                      <h2 className="text-xl md:text-2xl font-bold text-ocean-800">
                        {service.title}
                      </h2>
                    </div>

                    <div className="md:col-span-2">
                      {service.image && (
                        <div className="mb-5 rounded-xl overflow-hidden">
                          <div className="w-full h-72 md:h-96">
                            <img
                              src={service.image}
                              alt={service.title}
                              className={`w-full h-full object-cover ${service.imagePosition ?? "object-center"}`}
                            />
                          </div>
                          {service.imageCaption && (
                            <p className="text-xs text-ocean-400 mt-2 italic">{service.imageCaption}</p>
                          )}
                        </div>
                      )}
                      <p className="text-ocean-600 text-sm md:text-base leading-relaxed mb-5">
                        {service.description}
                      </p>
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-sm font-medium text-ocean-500 hover:text-ocean-700 transition-colors"
                      >
                        <span>お問い合わせ相談する</span>
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
